import { useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { formatPrice, Product, productDetailRoute } from '../data/products';
import { SiteLink } from './SiteLink';
type ProductListRowProps = {
  product: Product;
  onAddToCart: (quantity: number) => void;
};

/**
 * Compact text-first product row for catalog items presented without a photo.
 * Shows the same information and actions as the card layout.
 */
export function ProductListRow({
  product,
  onAddToCart
}: ProductListRowProps) {
  const [quantity, setQuantity] = useState(1);
  const quantityId = `row-qty-${product.sku}`;
  const isOutOfStock = product.availability.toLowerCase().includes('out of stock');
  return <li className="border-b border-ink/15 py-5" data-id="element-331" data-mp="2b613utu2wceq">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8" data-id="element-332" data-mp="2b5rbx0ph3v6e">
        <div className="min-w-0" data-id="element-333" data-mp="buq9cbkjqhid">
          <h3 className="text-[17px] font-semibold leading-6 text-ink" data-id="element-334" data-mp="gs9wk0z824mg">
            <SiteLink to={productDetailRoute} className="transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-335" data-mp="gsi9fsvch9xn">
              {product.name}
            </SiteLink>
          </h3>
          <p className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-336" data-mp="1k70qae6vdtyt">
            <span className="text-[19px] font-semibold text-ink" data-id="element-337" data-mp="1k4wnjzkpj104">
              {formatPrice(product.price)}
            </span>
            <span className="text-[14.5px] text-ink/60" data-id="element-338" data-mp="2bxwzn4tq1ghc">SKU {product.sku}</span>
            <span className={`text-[14.5px] font-medium ${isOutOfStock ? 'text-ink/60' : 'text-pine'}`} data-id="element-339" data-mp="2bvuccsjye1gb">
              {product.availability}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 lg:shrink-0" data-id="element-340" data-mp="1xxcise7d9uqh">
          {isOutOfStock ? <div className="h-11" aria-hidden="true" data-id="element-341" data-mp="rjd4wwxryzb4" /> : <div className="flex items-center gap-2" data-id="element-342" data-mp="mjijh8wrr1xg">
              <label htmlFor={quantityId} className="text-[14.5px] font-medium text-ink/75" data-id="element-343" data-mp="mhxmzdqw98wy">
                Qty
              </label>
              <div className="flex items-center rounded-btn border border-ink/25" data-id="element-344" data-mp="sz8w0fke540v">
                <button type="button" aria-label={`Decrease quantity of ${product.name}`} onClick={() => setQuantity(value => Math.max(1, value - 1))} className="flex h-11 w-11 items-center justify-center text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-345" data-mp="sx4ta0y8ab26">
                  <MinusIcon className="h-4 w-4" aria-hidden="true" data-id="element-346" data-mp="a27ooo3d6rtr" />
                </button>
                <input id={quantityId} type="number" min={1} inputMode="numeric" value={quantity} onChange={event => {
              const next = Number(event.target.value);
              setQuantity(Number.isFinite(next) && next >= 1 ? Math.floor(next) : 1);
            }} className="h-11 w-12 border-x border-ink/25 bg-white text-center text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pegasus-blue/40" data-id="element-347" data-mp="3sfxctkrfcg46" />
                <button type="button" aria-label={`Increase quantity of ${product.name}`} onClick={() => setQuantity(value => value + 1)} className="flex h-11 w-11 items-center justify-center text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-348" data-mp="3dsoxc7r31ome">
                  <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-349" data-mp="d102i6ox3y0" />
                </button>
              </div>
            </div>}

          <button type="button" disabled={isOutOfStock} aria-disabled={isOutOfStock || undefined} onClick={() => onAddToCart(quantity)} className={`inline-flex min-h-[48px] items-center justify-center whitespace-nowrap rounded-btn px-5 text-[16px] font-bold transition-colors duration-150 ease-out ${isOutOfStock ? 'cursor-not-allowed border border-ink/20 bg-ink/5 text-ink/55' : 'bg-pegasus-magenta text-white hover:bg-[#bb2f83]'}`} data-id="element-350" data-mp="3rkdckjwmot7l">
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </button>

          <SiteLink to={productDetailRoute} className="inline-flex min-h-[44px] items-center whitespace-nowrap text-[15px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-351" data-mp="2dljtlgghca3q">
            View Details
          </SiteLink>
        </div>
      </div>
    </li>;
}